/**
 * Единый конфиг типов шаблонов сообщений — ЕДИНСТВЕННЫЙ источник истины
 * для backend и admin о том, какие поля/каналы/плейсхолдеры допустимы для
 * каждого типа шаблона.
 *
 * Соответствует pgEnum `MessageTemplateType` / `MessageTemplateChannel`
 * в packages/db/src/drizzle/schema/enums.ts. Значения здесь —
 * самостоятельный `as const` источник (не зависит от Drizzle), что
 * позволяет импортировать конфиг в admin без тяги @masters-club/db.
 */

/** Тип шаблона сообщения. Зеркало pgEnum `MessageTemplateType`. */
export const MessageTemplateType = {
  /** OTP-код (письмо). */
  OTP: 'OTP',
  /** Приглашение администратора (письмо). */
  ADMIN_INVITE: 'ADMIN_INVITE',
  /** Сброс пароля администратора (письмо). */
  ADMIN_RESET: 'ADMIN_RESET',
  /** Общее уведомление (письмо). */
  NOTIFICATION: 'NOTIFICATION',
  /** Платёжная ссылка по заказу (письмо / Telegram-бот / Telegram-сессия). */
  ORDER_INVOICE: 'ORDER_INVOICE',
  /** Приглашение пользователя на платформу — email-канал (письмо). */
  INVITATION_EMAIL: 'INVITATION_EMAIL',
  /** Приглашение пользователя — Telegram-канал (именованные multi-шаблоны). */
  INVITATION_TELEGRAM: 'INVITATION_TELEGRAM',
} as const;

export type MessageTemplateType =
  (typeof MessageTemplateType)[keyof typeof MessageTemplateType];

/** Канал доставки. Зеркало pgEnum `MessageTemplateChannel`. */
export const MessageTemplateChannel = {
  EMAIL: 'EMAIL',
  TELEGRAM: 'TELEGRAM',
  TELEGRAM_SESSION: 'TELEGRAM_SESSION',
} as const;

export type MessageTemplateChannel =
  (typeof MessageTemplateChannel)[keyof typeof MessageTemplateChannel];

/**
 * Множественность шаблонов типа:
 * - `singleton` — ровно один шаблон на (тип, язык), авторезолв по типу+языку
 *   (письма OTP, ADMIN_INVITE, ADMIN_RESET, NOTIFICATION, ORDER_INVOICE,
 *   INVITATION_EMAIL).
 * - `multi-named` — несколько именованных шаблонов одного типа со `slug`/`name`
 *   и флагом `isDefault`, выбор шаблона по slug/id (Telegram-приглашения).
 */
export type TemplateMultiplicity = 'singleton' | 'multi-named';

/** Редактируемое в админке поле шаблона. */
export type TemplateEditableField =
  | 'subject'
  | 'docJson'
  | 'htmlTemplate'
  | 'name'
  | 'slug'
  | 'isDefault'
  | 'isActive';

/** Конфигурация одного типа шаблона. */
export interface MessageTemplateTypeConfig {
  /** Человекочитаемое название типа (для admin-UI). */
  readonly label: string;
  /** Краткое описание назначения. */
  readonly description: string;
  /** Множественность: один на (тип,язык) либо много именованных. */
  readonly multiplicity: TemplateMultiplicity;
  /** Каналы доставки, допустимые для этого типа. */
  readonly channels: readonly MessageTemplateChannel[];
  /** Нужен ли язык (singleton-письма — да; TG-приглашения — нет, sentinel 'ru'). */
  readonly requiresLang: boolean;
  /** Нужна ли тема (письма — да; Telegram-сообщения — нет). */
  readonly requiresSubject: boolean;
  /** Поля, которые админ может редактировать для этого типа. */
  readonly editableFields: readonly TemplateEditableField[];
  /** Допустимые Handlebars-плейсхолдеры (для подсказки и валидации). */
  readonly placeholders: readonly string[];
  /** Тестовые значения плейсхолдеров для предпросмотра. */
  readonly previewVars: Readonly<Record<string, string>>;
}

/**
 * Exhaustive `Record<MessageTemplateType, ...>` — компилятор требует запись
 * на каждый тип. Добавление значения в `MessageTemplateType` без записи здесь
 * — ошибка типизации (как существующий SAMPLE_VARS в email-templates.service).
 */
export const MESSAGE_TEMPLATE_CONFIG: Record<
  MessageTemplateType,
  MessageTemplateTypeConfig
> = {
  [MessageTemplateType.OTP]: {
    label: 'OTP-код',
    description: 'Письмо с одноразовым кодом входа.',
    multiplicity: 'singleton',
    channels: [MessageTemplateChannel.EMAIL],
    requiresLang: true,
    requiresSubject: true,
    editableFields: ['subject', 'docJson', 'htmlTemplate', 'isActive'],
    placeholders: ['{{code}}'],
    previewVars: { code: '123456' },
  },
  [MessageTemplateType.ADMIN_INVITE]: {
    label: 'Приглашение администратора',
    description: 'Письмо-приглашение в админ-панель.',
    multiplicity: 'singleton',
    channels: [MessageTemplateChannel.EMAIL],
    requiresLang: true,
    requiresSubject: true,
    editableFields: ['subject', 'docJson', 'htmlTemplate', 'isActive'],
    placeholders: ['{{url}}'],
    previewVars: { url: 'https://example.com/invite?token=sample' },
  },
  [MessageTemplateType.ADMIN_RESET]: {
    label: 'Сброс пароля администратора',
    description: 'Письмо со ссылкой на сброс пароля админа.',
    multiplicity: 'singleton',
    channels: [MessageTemplateChannel.EMAIL],
    requiresLang: true,
    requiresSubject: true,
    editableFields: ['subject', 'docJson', 'htmlTemplate', 'isActive'],
    placeholders: ['{{url}}'],
    previewVars: { url: 'https://example.com/reset?token=sample' },
  },
  [MessageTemplateType.NOTIFICATION]: {
    label: 'Уведомление',
    description: 'Общее письмо-уведомление.',
    multiplicity: 'singleton',
    channels: [MessageTemplateChannel.EMAIL],
    requiresLang: true,
    requiresSubject: true,
    editableFields: ['subject', 'docJson', 'htmlTemplate', 'isActive'],
    placeholders: ['{{subject}}', '{{body}}'],
    previewVars: {
      subject: 'Пример уведомления',
      body: 'Это пример тела уведомления для предпросмотра.',
    },
  },
  [MessageTemplateType.ORDER_INVOICE]: {
    label: 'Платёжная ссылка заказа',
    description:
      'Сообщение с платёжной ссылкой по заказу. Каналы: письмо, Telegram-бот, Telegram-сессия.',
    multiplicity: 'singleton',
    channels: [
      MessageTemplateChannel.EMAIL,
      MessageTemplateChannel.TELEGRAM,
      MessageTemplateChannel.TELEGRAM_SESSION,
    ],
    requiresLang: true,
    requiresSubject: true,
    editableFields: ['subject', 'docJson', 'htmlTemplate', 'isActive'],
    placeholders: [
      '{{orderId}}',
      '{{amount}}',
      '{{currency}}',
      '{{paymentUrl}}',
      '{{fullName}}',
    ],
    previewVars: {
      orderId: '72',
      amount: '83.43',
      currency: 'USD',
      paymentUrl: 'https://example.com/invoice/sample',
      fullName: 'Ренат',
    },
  },
  [MessageTemplateType.INVITATION_EMAIL]: {
    label: 'Приглашение на платформу (email)',
    description: 'Письмо-приглашение пользователя на платформу.',
    multiplicity: 'singleton',
    channels: [MessageTemplateChannel.EMAIL],
    requiresLang: true,
    requiresSubject: true,
    editableFields: ['subject', 'docJson', 'htmlTemplate', 'isActive'],
    placeholders: ['{{url}}', '{{firstName}}', '{{platformName}}'],
    previewVars: {
      url: 'https://example.com/invite/sample',
      firstName: 'Ренат',
      platformName: 'Masters Club',
    },
  },
  [MessageTemplateType.INVITATION_TELEGRAM]: {
    label: 'Приглашение в Telegram',
    description:
      'Именованные шаблоны Telegram-приглашений. Несколько шаблонов, один по умолчанию.',
    multiplicity: 'multi-named',
    channels: [MessageTemplateChannel.TELEGRAM],
    requiresLang: false,
    requiresSubject: false,
    editableFields: ['name', 'slug', 'htmlTemplate', 'isDefault', 'isActive'],
    placeholders: [
      '{{firstName}}',
      '{{lastName}}',
      '{{username}}',
      '{{botLink}}',
      '{{platformName}}',
    ],
    previewVars: {
      firstName: 'Ренат',
      lastName: 'Хайруллин',
      username: 'renat',
      botLink: '<a href="https://t.me/bot?start=invite_sample">Перейти к боту</a>',
      platformName: 'Masters Club',
    },
  },
};

/** Sentinel-язык для типов без локализации (multi-named Telegram-шаблоны). */
export const TEMPLATE_LANG_SENTINEL = 'ru';

/** Конфиг типа шаблона. */
export const getTemplateConfig = (
  type: MessageTemplateType,
): MessageTemplateTypeConfig => MESSAGE_TEMPLATE_CONFIG[type];

/** Допустимые каналы доставки для типа. */
export const channelsFor = (
  type: MessageTemplateType,
): readonly MessageTemplateChannel[] => MESSAGE_TEMPLATE_CONFIG[type].channels;

/** Допустимые плейсхолдеры для типа. */
export const placeholdersFor = (
  type: MessageTemplateType,
): readonly string[] => MESSAGE_TEMPLATE_CONFIG[type].placeholders;

/** Разрешён ли канал для типа. */
export const isChannelAllowed = (
  type: MessageTemplateType,
  channel: MessageTemplateChannel,
): boolean => MESSAGE_TEMPLATE_CONFIG[type].channels.includes(channel);

/** Является ли тип multi-named (именованные шаблоны со slug/isDefault). */
export const isMultiNamed = (type: MessageTemplateType): boolean =>
  MESSAGE_TEMPLATE_CONFIG[type].multiplicity === 'multi-named';

/** Type guard: строка — валидный MessageTemplateType (литеральная проверка). */
export const isMessageTemplateType = (
  value: string,
): value is MessageTemplateType =>
  value === MessageTemplateType.OTP ||
  value === MessageTemplateType.ADMIN_INVITE ||
  value === MessageTemplateType.ADMIN_RESET ||
  value === MessageTemplateType.NOTIFICATION ||
  value === MessageTemplateType.ORDER_INVOICE ||
  value === MessageTemplateType.INVITATION_EMAIL ||
  value === MessageTemplateType.INVITATION_TELEGRAM;

/** Все типы — для итерации в admin/seed. */
export const ALL_MESSAGE_TEMPLATE_TYPES: readonly MessageTemplateType[] =
  Object.values(MessageTemplateType);
